import type Owner from '@ember/owner'
import { setOwner } from '@ember/owner'
import Service, { service } from '@ember/service'
import { tracked } from '@glimmer/tracking'
import type ImpressionService from 'teacher-ui-client/services/impression'
import type SessionService from 'teacher-ui-client/services/session'

export enum ReadingEggsReportPath {
  StandardsPerformance = 'reporting.ui-scope.re.standards-performance',
  LessonPerformance = 'reporting.ui-scope.re.lesson-performance',
}

export enum ReadingEggspressReportPath {
  StandardsPerformance = 'reporting.ui-scope.rex.standards-performance',
  LessonPerformance = 'reporting.ui-scope.rex.lesson-performance',
}

export enum MathseedsReportPath {
  StandardsPerformance = 'reporting.ui-scope.maths.standards-performance',
  LessonPerformance = 'reporting.ui-scope.maths.lesson-performance',
}

export enum FastPhonicsReportPath {
  StandardsPerformance = 'reporting.ui-scope.fast-phonics.standards-performance',
  LessonPerformance = 'reporting.ui-scope.fast-phonics.lesson-performance',
}

/**
 * A feature notification with optional context to determine visibility.
 */
abstract class FeatureNotification {
  abstract isVisible(context?: Record<string, unknown>): boolean

  constructor(owner: Owner) {
    setOwner(this, owner)
  }
}

export enum FeatureNotificationId {
  SetAcademicStandards = 'set-academic-standards',
  NewReportForPath = 'new-report-for-path',
}

/**
 * A service for the conditional display of new feature notifications.
 *
 * Add a feature notification by writing a class that extends
 * `FeatureNotification`.
 *
 * - The class instance should be registered in this service's constructor.
 * - The class should have an `id` property from the FeatureNotificationId enum.
 * - The class should implement the `isVisible` method to determine visibility.
 *
 * Then query the service for notification visibility:
 *
 * @example
 * ```ts
 *   get showNotification() {
 *     return this.featureNotification.isVisible(FeatureNotificationId.YourNotificationId, { your: 'context' })
 *   }
 * ```
 *
 * or use the FeatureNotification helper:
 *
 * @example
 * ```gts
 * {{#if (FeatureNotification FeatureNotificationId.YourNotificationId your="context")}}
 *  <div>Your notification content</div>
 * {{/if}
 * ```
 */
export default class FeatureNotificationService extends Service {
  @tracked
  protected notifications: Record<FeatureNotificationId, FeatureNotification>

  constructor(owner: Owner) {
    super(owner)
    this.notifications = {
      [FeatureNotificationId.SetAcademicStandards]: new SetAcademicStandards(owner),
      [FeatureNotificationId.NewReportForPath]: new NewReportForPath(owner),
    }
  }

  isVisible(id: FeatureNotificationId, params?: Record<string, unknown>) {
    return this.notifications[id].isVisible(params)
  }
}

declare module '@ember/service' {
  interface Registry {
    'feature-notification': FeatureNotificationService
  }
}

class SetAcademicStandards extends FeatureNotification {
  @service session!: SessionService

  isVisible() {
    return !this.session.currentSchool.standardsSet
  }
}

class NewReportForPath extends FeatureNotification {
  @service impression!: ImpressionService

  isVisible({ reportPath }: Record<string, unknown>) {
    return typeof reportPath === 'string' && this.impression.notMade(reportPath)
  }
}
